import { useState, useEffect, useRef } from 'react';
import { useForm } from '@mantine/form';
import { Select, Button, Drawer, Group, Text, Box, ActionIcon, Tooltip, Paper } from '@mantine/core';
import { DatePickerInput, TimeInput } from '@mantine/dates';
import {
  IconPencil,
  IconTrash,
  IconX,
  IconCheck,
  IconClock,
  IconInfoCircle,
  IconRefresh,
  IconClockEdit,
  IconCalendar,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useMediaQuery } from '@mantine/hooks';
import timezoneData from '../entrypoints/utilities/timezones.json';

interface RecurringEventFieldsProps {
  form: any;
}

interface ModifiedOccurrence {
  originalDateId: number;
  date: Date;
  startTime: string;
  endTime: string;
  isSkipped?: boolean;
  spansNextDay?: boolean;
}

const TIMEZONE_OPTIONS = Object.entries(timezoneData).map(([value, label]) => ({
  value,
  label: `${label} (${value})`,
}));

export default function RecurringEventFields({ form }: RecurringEventFieldsProps) {
  const { start_date, start_time, end_time, recurrence_pattern, num_occurrences, spans_next_day } =
    form.values.recurring_event_attributes;

  const [recurrenceDates, setRecurrenceDates] = useState<Date[]>([]);
  const [modifiedOccurrences, setModifiedOccurrences] = useState<ModifiedOccurrence[]>([]);
  const [selectedOccurrence, setSelectedOccurrence] = useState<Date | null>(null);
  const [originalDateId, setOriginalDateId] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState({ start: '', end: '' });
  const [eventSpansNextDay, setEventSpansNextDay] = useState(false);
  const [occurrenceSpansNextDay, setOccurrenceSpansNextDay] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [ordinalOccurrence, setOrdinalOccurrence] = useState('');
  const [opened, setOpened] = useState(true);
  const [infoOpened, setInfoOpened] = useState(false);

  const dayOfWeek = start_date ? `${dayjs(start_date).format('dddd')}` : '';

  const startTimeRef = useRef<HTMLInputElement>(null);
  const endTimeRef = useRef<HTMLInputElement>(null);
  const drawerStartTimeRef = useRef<HTMLInputElement>(null);
  const drawerEndTimeRef = useRef<HTMLInputElement>(null);

  const getOrdinal = (n: number) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const startPickerControl = (
    <ActionIcon variant="subtle" color="gray" onClick={() => startTimeRef.current?.showPicker()}>
      <IconClock size={16} stroke={1.5} />
    </ActionIcon>
  );
  const endPickerControl = (
    <ActionIcon variant="subtle" color="gray" onClick={() => endTimeRef.current?.showPicker()}>
      <IconClock size={16} stroke={1.5} />
    </ActionIcon>
  );

  const drawerStartPickerControl = (
    <ActionIcon variant="subtle" color="gray" onClick={() => drawerStartTimeRef.current?.showPicker()}>
      <IconClock size={16} stroke={1.5} />
    </ActionIcon>
  );
  const drawerEndPickerControl = (
    <ActionIcon variant="subtle" color="gray" onClick={() => drawerEndTimeRef.current?.showPicker()}>
      <IconClock size={16} stroke={1.5} />
    </ActionIcon>
  );

  const getFinalOccurrencesForSubmission = () => {
    return recurrenceDates
      .filter((date) => {
        const dateId = date.getTime();
        const mod = modifiedOccurrences.find((occ) => occ.originalDateId === dateId || occ.date.getTime() === dateId);
        return !(mod && mod.isSkipped);
      })
      .map((date) => {
        const dateId = date.getTime();
        const mod = modifiedOccurrences.find(
          (occ) => (occ.originalDateId === dateId || occ.date.getTime() === dateId) && !occ.isSkipped,
        );
        return {
          date: date.toISOString().split('T')[0],
          start_time: mod ? mod.startTime : start_time,
          end_time: mod ? mod.endTime : end_time,
          spans_next_day: mod ? (mod.spansNextDay ?? spans_next_day) : spans_next_day,
        };
      });
  };

  const getMaxOccurrencesForPattern = (pattern: string) => {
    switch (pattern) {
      case 'Weekly':
        return 12;
      case 'Every Two Weeks':
        return 6;
      case 'Monthly':
        return 3;
      default:
        return 12;
    }
  };

  // 1) Clamp num_occurrences when pattern changes
  useEffect(() => {
    const maxAllowed = getMaxOccurrencesForPattern(recurrence_pattern);
    const current = num_occurrences || 0;
    if (current === 0 || current > maxAllowed) {
      form.setFieldValue('recurring_event_attributes.num_occurrences', maxAllowed);
    }
  }, [recurrence_pattern, num_occurrences, form]);

  // 2) Compute recurrenceDates & ordinalOccurrence
  useEffect(() => {
    const maxAllowed = getMaxOccurrencesForPattern(recurrence_pattern);
    const current = num_occurrences || 0;
    if (current === 0 || current > maxAllowed) {
      form.setFieldValue('recurring_event_attributes.num_occurrences', maxAllowed);
    }
  }, [recurrence_pattern, num_occurrences, form]);

  // 2) Compute recurrenceDates & ordinalOccurrence
  useEffect(() => {
    if (!start_date || !num_occurrences) {
      setRecurrenceDates([]);
      setOrdinalOccurrence('');
      return;
    }
    const base = dayjs(start_date);
    const dates: Date[] = [];

    if (recurrence_pattern === 'Weekly' || recurrence_pattern === 'Every Two Weeks') {
      const step = recurrence_pattern === 'Weekly' ? 7 : 14;
      for (let i = 0; i < num_occurrences; i++) {
        dates.push(base.add(i * step, 'day').toDate());
      }
    } else if (recurrence_pattern === 'Monthly') {
      const weekday = base.day();
      const weekOfMonth = Math.ceil(base.date() / 7);
      for (let i = 0; i < num_occurrences; i++) {
        let d = base.add(i, 'month').startOf('month');
        d = d.day(weekday);
        if (d.month() !== base.add(i, 'month').month()) d = d.add(1, 'week');
        d = d.add(weekOfMonth - 1, 'week');
        dates.push(d.toDate());
      }
      setOrdinalOccurrence(getOrdinal(weekOfMonth));
    }
    setRecurrenceDates(dates);
  }, [start_date, recurrence_pattern, num_occurrences]);

  // 3) Sync final occurrences back to form
  useEffect(() => {
    const finalOccurrences = getFinalOccurrencesForSubmission();
    form.setFieldValue('recurring_event_occurrences', finalOccurrences);
  }, [recurrenceDates, modifiedOccurrences, start_time, end_time, form]);

  // 4) Set the values of ordinalOccurrence and Occurrence Day
  useEffect(() => {
    if (recurrence_pattern === 'Monthly' && ordinalOccurrence && dayOfWeek) {
      form.setFieldValue(
        'recurring_event_attributes.ordinal_occurrence',
        // turn "3rd" into "Third", or keep as-is if you like:
        ordinalOccurrence,
      );
      form.setFieldValue(
        'recurring_event_attributes.occurrence_day',
        // e.g. "Saturday"
        dayOfWeek,
      );
    }
  }, [recurrence_pattern, ordinalOccurrence, dayOfWeek, form]);

  const handleModify = (date: Date) => {
    const id = date.getTime();
    setOriginalDateId(id);
    setSelectedOccurrence(date);
    const mod = modifiedOccurrences.find((occ) => occ.originalDateId === id);
    if (mod) {
      setSelectedTime({ start: mod.startTime, end: mod.endTime });
      setOccurrenceSpansNextDay(mod.spansNextDay ?? spans_next_day);
    } else {
      setSelectedTime({ start: start_time, end: end_time });
      setOccurrenceSpansNextDay(spans_next_day);
    }
    setDrawerOpen(true);
  };

  const saveModification = () => {
    if (!selectedOccurrence || originalDateId === null) return;
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(selectedTime.start) || !timeRegex.test(selectedTime.end)) return;

    const dateChanged = selectedOccurrence.getTime() !== originalDateId;
    const timeChanged = selectedTime.start !== start_time || selectedTime.end !== end_time;
    const spansChanged = occurrenceSpansNextDay !== spans_next_day;
    if (!(dateChanged || timeChanged || spansChanged)) {
      setDrawerOpen(false);
      return;
    }

    const upd: ModifiedOccurrence = {
      originalDateId,
      date: selectedOccurrence,
      startTime: selectedTime.start,
      endTime: selectedTime.end,
      spansNextDay: occurrenceSpansNextDay,
    };

    setModifiedOccurrences((mods) => {
      const idx = mods.findIndex((o) => o.originalDateId === originalDateId);
      if (idx >= 0) {
        const copy = [...mods];
        copy[idx] = upd;
        return copy;
      }
      return [...mods, upd];
    });

    if (dateChanged) {
      setRecurrenceDates((dates) => dates.map((d) => (d.getTime() === originalDateId ? selectedOccurrence : d)));
    }

    setDrawerOpen(false);
  };

  const isOccurrenceModified = (date: Date) =>
    modifiedOccurrences.some(
      (o) => (o.originalDateId === date.getTime() || o.date.getTime() === date.getTime()) && !o.isSkipped,
    );

  const isOccurrenceSkipped = (date: Date) =>
    modifiedOccurrences.some(
      (o) => (o.originalDateId === date.getTime() || o.date.getTime() === date.getTime()) && o.isSkipped,
    );

  const getOccurrenceTime = (date: Date) => {
    const id = date.getTime();
    const mod = modifiedOccurrences.find((o) => (o.originalDateId === id || o.date.getTime() === id) && !o.isSkipped);
    if (mod) {
      return { start: mod.startTime, end: mod.endTime, spansNextDay: mod.spansNextDay ?? spans_next_day };
    }
    return { start: start_time, end: end_time, spansNextDay: spans_next_day };
  };

  const formatTimeToAMPM = (t: string) => (t ? dayjs(`2025-01-01 ${t}`, 'YYYY-MM-DD HH:mm').format('h:mm A') : '');

  const handleRestoreOccurrence = (date: Date) => {
    const dateId = date.getTime();
    setModifiedOccurrences(
      modifiedOccurrences.filter((occ) => !(occ.originalDateId === dateId || occ.date.getTime() === dateId)),
    );

    const modifiedOcc = modifiedOccurrences.find(
      (occ) => occ.originalDateId === dateId || occ.date.getTime() === dateId,
    );
    if (modifiedOcc && modifiedOcc.originalDateId !== modifiedOcc.date.getTime()) {
      setRecurrenceDates((prevDates) =>
        prevDates.map((d) => (d.getTime() === modifiedOcc.date.getTime() ? new Date(modifiedOcc.originalDateId) : d)),
      );
    }
  };

  return (
    <div>
      <Group grow mt="md">
        <DatePickerInput
          label="Start Date"
          placeholder="Select date"
          name="recurring_event_attributes.start_date"
          value={start_date ? new Date(start_date) : null}
          onChange={(date) =>
            form.setFieldValue('recurring_event_attributes.start_date', date ? date.toISOString() : '')
          }
          required
          styles={{ label: { color: '#FFF7ED' } }}
        />
        <Select
          label="Recurrence Pattern"
          name="recurring_event_attributes.recurrence_pattern"
          data={['Weekly', 'Every Two Weeks', 'Monthly']}
          value={recurrence_pattern}
          onChange={(v) => form.setFieldValue('recurring_event_attributes.recurrence_pattern', v || '')}
          styles={{ label: { color: '#FFF7ED' } }}
        />
      </Group>

      <Group mt="md">
        <Box style={{ flex: 1, minWidth: 135 }}>
          <TimeInput
            label="Start Time"
            name="recurring_event_attributes.start_time"
            value={start_time}
            onChange={(e) => form.setFieldValue('recurring_event_attributes.start_time', e.target.value)}
            rightSection={startPickerControl}
            ref={startTimeRef}
            required
            styles={{ label: { color: '#FFF7ED' } }}
          />
        </Box>
        <Box style={{ flex: 1, minWidth: 150 }}>
          <TimeInput
            label="End Time"
            name="recurring_event_attributes.end_time"
            value={end_time}
            onChange={(e) => form.setFieldValue('recurring_event_attributes.end_time', e.target.value)}
            rightSection={endPickerControl}
            ref={endTimeRef}
            required
            styles={{ label: { color: '#FFF7ED' } }}
          />
        </Box>
      </Group>

      <Group mt="md">
        <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: isMobile ? 200 : undefined }}>
          <Group gap="xs" mb={-8} mt={-4}>
            <Text size="sm" className="mb-1" fw={500} style={{ color: '#FFF7ED' }}>
              Next Day?
            </Text>
            <Tooltip
              label="Select 'Ends Next Day' if your occurrence continues past midnight or ends same day if it ends before midnight."
              className="mb-1"
              events={{ hover: true, focus: false, touch: true }}
              multiline
              w={300}
              withArrow
              color="orange"
              opened={infoOpened}
            >
              <ActionIcon variant="transparent" color="orange" size="sm" onClick={() => setInfoOpened((o) => !o)}>
                <IconInfoCircle size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>

          <Select
            className="mt-2"
            data={['Ends Same Day', 'Ends Next Day']}
            value={eventSpansNextDay ? 'Ends Next Day' : 'Ends Same Day'}
            onChange={(v) => setEventSpansNextDay(v === 'Ends Next Day')}
            styles={{ label: { color: '#FFF7ED' } }}
            clearable
          />
        </Box>
        <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: isMobile ? 200 : undefined }}>
          <Group gap="xs" mb={-8} mt={-4}>
            <Text size="sm" className="mb-1" fw={500} style={{ color: '#FFF7ED' }}>
              # of Occurrences
            </Text>
            <Tooltip
              label="The maximum number of occurrences for recurring events is 90 days. 12 occurrences for weekly, 6 for bi-weekly, and 3 for monthly."
              className="mb-1"
              multiline
              w={300}
              withArrow
              color="orange"
              opened={opened}
            >
              <ActionIcon variant="transparent" color="orange" size="sm" onClick={() => setOpened((o) => !o)}>
                <IconInfoCircle size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>

          <Select
            className="mt-2"
            name="recurring_event_attributes.num_occurrences"
            data={
              recurrence_pattern === 'Weekly'
                ? Array.from({ length: 11 }, (_, i) => (i + 2).toString())
                : recurrence_pattern === 'Every Two Weeks'
                  ? Array.from({ length: 5 }, (_, i) => (i + 2).toString())
                  : Array.from({ length: 2 }, (_, i) => (i + 2).toString())
            }
            value={(num_occurrences || 0).toString()}
            onChange={(v) => form.setFieldValue('recurring_event_attributes.num_occurrences', parseInt(v, 10))}
            styles={{ label: { color: '#FFF7ED' } }}
          />
        </Box>
      </Group>

      {/* Show dynamic header only for monthly pattern */}
      {recurrence_pattern === 'Monthly' && recurrenceDates.length > 0 && (
        <Paper shadow="xs" withBorder p="sm" mt="md" style={{ background: '#17171750', borderColor: '#EA580C' }}>
          <Group align="center" wrap="nowrap">
            <IconCalendar size={20} color="#EA580C" />
            <Text c="white" fw={700} size="xs" style={{}}>
              This is a monthly recurring event that occurs on the {ordinalOccurrence} {dayOfWeek} of each month.
            </Text>
          </Group>
        </Paper>
      )}

      {recurrenceDates.map((date, i) => {
        const skipped = isOccurrenceSkipped(date);
        const modified = isOccurrenceModified(date);
        const { start, end } = getOccurrenceTime(date);
        return (
          <Paper
            key={i}
            shadow="xs"
            withBorder
            p="xs"
            mt="sm"
            style={{ opacity: skipped ? 0.5 : 1, background: 'transparent' }}
          >
            <Group justify="space-between">
              <Group>
                <Text size="sm" fw={500} c="#FFF7ED" style={{ textDecoration: skipped ? 'line-through' : 'none' }}>
                  {dayjs(date).format('ddd, MMM D, YYYY')} -{' '}
                  {skipped ? 'SKIPPED' : `${formatTimeToAMPM(start)} to ${formatTimeToAMPM(end)}`}
                </Text>
                {modified && (
                  <Tooltip label="Modified">
                    <IconClockEdit size={18} color="orange" />
                  </Tooltip>
                )}
                {skipped && (
                  <Tooltip label="Skipped">
                    <IconX size={18} color="red" />
                  </Tooltip>
                )}
              </Group>
              <Group>
                {!skipped && (
                  <Button variant="outline" size="md" onClick={() => handleModify(date)}>
                    <IconPencil />
                  </Button>
                )}
                {(modified || skipped) && (
                  <Button color="orange" size="md" onClick={() => handleRestoreOccurrence(date)}>
                    <IconRefresh />
                  </Button>
                )}
                {!skipped && (
                  <Button
                    color="red"
                    size="md"
                    onClick={() => {
                      setModifiedOccurrences((mods) => [
                        ...mods,
                        { originalDateId: date.getTime(), date, startTime: start, endTime: end, isSkipped: true },
                      ]);
                    }}
                  >
                    <IconTrash />
                  </Button>
                )}
              </Group>
            </Group>
          </Paper>
        );
      })}

      <Drawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Modify Occurrence"
        position="right"
        styles={{
          content: { backgroundColor: '#3d3c3e' },
          header: { backgroundColor: '#2c2b2d', marginBottom: '2rem' },
          title: { color: '#f2f2f2', fontWeight: 600, fontSize: '1.5rem' },
        }}
      >
        <DatePickerInput
          label="Occurrence Date"
          value={selectedOccurrence}
          onChange={(d) => setSelectedOccurrence(d)}
          styles={{ label: { color: '#FFF7ED' } }}
        />
        <TimeInput
          label="Start Time"
          value={selectedTime.start}
          onChange={(e) => setSelectedTime((s) => ({ ...s, start: e.target.value }))}
          rightSection={drawerStartPickerControl}
          ref={drawerStartTimeRef}
          styles={{ label: { color: '#FFF7ED' } }}
          mt="md"
        />
        <TimeInput
          label="End Time"
          value={selectedTime.end}
          onChange={(e) => setSelectedTime((s) => ({ ...s, end: e.target.value }))}
          rightSection={drawerEndPickerControl}
          ref={drawerEndTimeRef}
          styles={{ label: { color: '#FFF7ED' } }}
          mt="md"
        />
        <Group justify="right" mt="xl">
          <Button color="gray" onClick={() => setDrawerOpen(false)}>
            Cancel
          </Button>
          <Button onClick={saveModification} leftSection={<IconCheck />}>
            Save
          </Button>
        </Group>
      </Drawer>
    </div>
  );
}
